import { useEffect } from 'react';
import { UseFormGetValues, UseFormSetValue } from 'react-hook-form';

import { searchCustomers } from 'lib/api/searchCustomers';
import { NormalizedLoad, TMSCustomer } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';

import {
  mgbCustomerNeedsHydrationSearch,
  resolveCustomerDisplayName,
  upsertTMSCustomer,
} from './utils';

type SetVisibleCustomers = (
  nextCustomers:
    | Maybe<TMSCustomer[]>
    | ((prevCustomers: Maybe<TMSCustomer[]>) => Maybe<TMSCustomer[]>)
) => void;

type UseMGBCustomerHydrationArgs = {
  tmsID: number;
  isCreateMode: boolean;
  isCustomersLoading: boolean;
  customerExternalTMSID: string | undefined;
  customers: Maybe<TMSCustomer[]>;
  customersByExternalID: Record<string, TMSCustomer>;
  setVisibleCustomers: SetVisibleCustomers;
  setValue: UseFormSetValue<NormalizedLoad>;
  getValues: UseFormGetValues<NormalizedLoad>;
};

/**
 * One-shot by-id TMS search when create-mode needs ediEnabled (or view mode is missing the row).
 * Driven by a boolean need flag — not mirrored list refs / attempt Set bookkeeping.
 * BE create still rejects EDI customers if FE metadata stays stale.
 */
export function useMGBCustomerHydration({
  tmsID,
  isCreateMode,
  isCustomersLoading,
  customerExternalTMSID,
  customers,
  customersByExternalID,
  setVisibleCustomers,
  setValue,
  getValues,
}: UseMGBCustomerHydrationArgs): void {
  const externalTMSID = customerExternalTMSID?.trim() ?? '';
  const needsCustomerHydration =
    Boolean(externalTMSID) &&
    !isCustomersLoading &&
    Boolean(tmsID) &&
    mgbCustomerNeedsHydrationSearch(
      externalTMSID,
      customersByExternalID,
      customers,
      isCreateMode
    );

  useEffect(() => {
    if (!needsCustomerHydration || !tmsID || !externalTMSID) {
      return;
    }

    let cancelled = false;

    const run = async () => {
      const res = await searchCustomers(tmsID, 'externalTMSID', externalTMSID);
      if (cancelled) {
        return;
      }

      const searched = res.isOk() ? (res.value.customerList ?? []) : [];
      const exactMatch = searched.find(
        (customer) => customer.externalTMSID === externalTMSID
      );
      if (!exactMatch) {
        return;
      }

      setVisibleCustomers((prev) => upsertTMSCustomer(prev ?? [], exactMatch));

      const displayName = resolveCustomerDisplayName(exactMatch);
      if (getValues('customer.name') !== displayName) {
        setValue('customer.name', displayName, {
          shouldDirty: isCreateMode,
          shouldValidate: true,
        });
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [
    needsCustomerHydration,
    externalTMSID,
    tmsID,
    isCreateMode,
    setVisibleCustomers,
    setValue,
    getValues,
  ]);
}
